import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/** App-canonical display timezone (CLAUDE.md "Dates & timezone"). */
export const APP_TZ = 'America/New_York';

/**
 * App-canonical DISPLAY formats. US numeric — `08/13/2026`, not `Aug 13, 2026`.
 *
 * Exported as constants, not repeated as literals, because a handful of places
 * legitimately format with dayjs directly (chart axis labels, the date-range
 * filter's own summary line) and those have to move when this moves. Anything
 * rendering a date for a user should call the helpers below rather than reach
 * for these; the constants exist for the cases the helpers can't cover.
 *
 * These are display-only. Values sent to the API stay `YYYY-MM-DD`, and
 * `<input type="date">` is always `YYYY-MM-DD` by spec — the browser decides how
 * to show it, and it already shows mm/dd/yyyy in a US locale.
 */
export const DATE_FMT = 'MM/DD/YYYY';
/** No year — for calendar surfaces where surrounding context supplies it. */
export const DATE_FMT_SHORT = 'MM/DD';
export const TIME_FMT = 'h:mm A';
export const TIME_FMT_SECONDS = 'h:mm:ss A';
export const TIME_FMT_24 = 'HH:mm';
export const TIME_FMT_24_SECONDS = 'HH:mm:ss';
export const DATETIME_FMT = `${DATE_FMT} ${TIME_FMT}`;
export const DATETIME_FMT_SECONDS = `${DATE_FMT} ${TIME_FMT_SECONDS}`;
export const DATETIME_FMT_24 = `${DATE_FMT} ${TIME_FMT_24}`;
export const DATETIME_FMT_24_SECONDS = `${DATE_FMT} ${TIME_FMT_24_SECONDS}`;
/** No year — chart axes / tooltips, where the surrounding window supplies it. */
export const DATETIME_FMT_SHORT = `${DATE_FMT_SHORT} ${TIME_FMT}`;

// Pure YYYY-MM-DD (DATEONLY columns / <input type="date"> values). These are
// calendar dates, not instants — never timezone-shift them or the displayed
// day drifts for non-Eastern viewers.
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

type DateInput = string | number | Date | null | undefined;

const parseForDisplay = (value: Exclude<DateInput, null | undefined>) => {
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    return dayjs(value); // calendar date — no tz conversion
  }
  // Timestamp (instant): stored UTC, displayed Eastern per the dates rule.
  return dayjs(value).tz(APP_TZ);
};

/**
 * "08/13/2026" — null-safe date display. Timestamps are bucketed into the
 * Eastern calendar day; pure YYYY-MM-DD strings render as-is.
 */
export const formatDate = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = parseForDisplay(value);
  return d.isValid() ? d.format(DATE_FMT) : 'Invalid date';
};

/**
 * "08/13/2026 3:42 PM ET" — timestamps shown in Eastern, labelled, per the
 * CLAUDE.md dates rule (display converts UTC → America/New_York).
 */
export const formatDateTime = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value).tz(APP_TZ);
  return d.isValid() ? `${d.format(DATETIME_FMT)} ET` : 'Invalid date';
};

/**
 * The calendar day a stored value DENOTES, as `YYYY-MM-DD` — the single place
 * that decides, so nothing has to recover a date by re-parsing a display string.
 * (`daysUntilStoredDay` and `formatStoredDateShort` used to regex `MM/DD/YYYY`
 * back apart, which quietly made `DATE_FMT` unchangeable despite this module
 * advertising it as the one edit point.)
 *
 * Some values are plain dates the database happens to store as DATETIME:
 * `billing_orders.source_completed_at` and `order_date` are the LIS `c.printed`
 * DATE, so they always sit at midnight UTC. Reading one as an instant converts
 * midnight UTC to 8:00 PM Eastern the PREVIOUS day — which is how
 * "Finalized (LIS)" showed every order a day early with a fabricated time.
 *
 * Answers the question across all three shapes a date reaches us in:
 *   - `YYYY-MM-DD` (DATEONLY: `date_of_service`, `lis_entered_at`) → as-is, so one
 *     call handles a mixed pair like `date_of_service || order_date`;
 *   - exactly midnight UTC → that UTC day. A pure date can only be stored in a
 *     DATETIME as midnight, so this is the date-valued case;
 *   - any other instant → its EASTERN day. `order_date` is a plain `DATE` column
 *     and the create API accepts any string, so a manually-entered 9 PM ET order
 *     is a real instant — reading THAT in UTC would show it a day late, the exact
 *     mirror of the bug this fixes.
 */
export const storedDayIso = (value: DateInput): string | null => {
  if (value === null || value === undefined || value === '') return null;
  // Shape isn't validity: `2026-02-30` and `2026-13-45` match the pattern. Passing
  // one straight through printed `13/45/2026` and made the day maths roll over
  // into a wrong month. Round-trip it, the way the backend's storedDayKey does.
  if (typeof value === 'string' && DATE_ONLY.test(value)) {
    const d = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value ? value : null;
  }
  const utcD = dayjs(value).utc();
  if (!utcD.isValid()) return null;
  const isMidnightUtc = utcD.hour() === 0 && utcD.minute() === 0
    && utcD.second() === 0 && utcD.millisecond() === 0;
  return (isMidnightUtc ? utcD : dayjs(value).tz(APP_TZ)).format('YYYY-MM-DD');
};

/**
 * A stored CALENDAR DAY plus `n` whole days, as `YYYY-MM-DD` — the day maths
 * behind a derived deadline (billing's timely filing limit: date of service +
 * the payer's filing window).
 *
 * Pure UTC arithmetic on the day key: `Date.UTC` normalizes month/year
 * rollover and leap days, and never consults a zone, so the answer can't drift
 * with DST. Adding days to the *instant* instead — `dayjs(dos).add(90, 'day')`
 * on a value stored at midnight UTC — reads it in the browser's zone first and
 * lands a day early for every viewer west of UTC, which is the whole reason
 * this module owns the day maths (CLAUDE.md dates rule).
 */
export const storedDayPlusDays = (value: DateInput, n: number): string | null => {
  const key = storedDayIso(value);
  if (!key || !Number.isFinite(n)) return null;
  const [y, m, d] = key.split('-').map(Number);
  const t = Date.UTC(y, m - 1, d + Math.trunc(n));
  return Number.isNaN(t) ? null : new Date(t).toISOString().slice(0, 10);
};

/** The Eastern calendar day of an INSTANT, as `YYYY-MM-DD`. */
export const etDayIso = (value: DateInput): string | null => {
  if (value === null || value === undefined || value === '') return null;
  const d = parseForDisplay(value);
  return d.isValid() ? d.format('YYYY-MM-DD') : null;
};

/**
 * `YYYY-MM-DD` → a display format. Goes through dayjs so the FORMAT CONSTANTS are
 * what decide the output: hand-assembling `${m}/${d}/${y}` here quietly made
 * `DATE_FMT` unchangeable for exactly the helpers that need it most, which is the
 * failure this module keeps being caught by. A bare `YYYY-MM-DD` parses as local
 * midnight and is only ever formatted, never converted, so no zone is involved.
 */
const isoToDisplay = (iso: string, fmt: string = DATE_FMT): string => dayjs(iso).format(fmt);

/**
 * The shared body of `formatStoredDate` / `formatStoredDateShort`: absent → the
 * caller's fallback, unparseable → "Invalid date", otherwise the stored day in
 * the requested format.
 *
 * One function because `storedDayIso` collapses "absent" and "unparseable" into
 * the same `null`, so each caller had to re-derive the emptiness test — and a
 * miss would make the short form print "Invalid date" where the long form
 * printed the fallback for the very same row.
 */
const renderStoredDay = (value: DateInput, fallback: string, fmt: string): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const iso = storedDayIso(value);
  return iso ? isoToDisplay(iso, fmt) : 'Invalid date';
};

/**
 * "08/13/2026" — a CALENDAR DATE that may arrive in a timestamp column. See
 * `storedDayIso` for which day it picks and why. Use `formatDateTime` when the
 * time itself is meaningful (`created_at`).
 */
export const formatStoredDate = (value: DateInput, fallback = '—'): string =>
  renderStoredDay(value, fallback, DATE_FMT);

/**
 * "Thu 08/13/2026" — a date whose WEEKDAY carries meaning: day-group headings on
 * the activity reports, where "was that a Saturday?" is half the question being
 * asked. Everything else should use `formatDate`; this exists so those two
 * surfaces don't hand-roll `toLocaleDateString(undefined, { weekday: … })`,
 * which formats in the BROWSER's locale and zone — the thing the rest of this
 * module exists to prevent.
 */
export const formatDateWithWeekday = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = parseForDisplay(value);
  return d.isValid() ? d.format(`ddd ${DATE_FMT}`) : 'Invalid date';
};

/**
 * Google Workspace never-signed-in test. `lastLoginTime` for such an account
 * comes back as the Unix EPOCH rather than null, so a plain null-check treats
 * `12/31/1969` as real data. Shared with `formatLastLogin` so the column that
 * prints "Never" and the filter that selects those rows can't disagree — they
 * were two independent copies of this rule, one of them evaluating in the
 * browser's zone.
 */
export const isNeverLoggedIn = (value: DateInput): boolean => {
  if (value === null || value === undefined || value === '') return true;
  const d = dayjs(value).tz(APP_TZ);
  return !d.isValid() || d.year() <= 1970;
};

/**
 * Google Workspace last-sign-in. `lastLoginTime` for an account that has NEVER
 * signed in comes back as the Unix epoch (`1970-01-01T00:00:00.000Z`) rather
 * than null, so a plain null-check renders `12/31/1969` — a date that looks like
 * data. Collapse the epoch to "Never" instead. Shared because two surfaces show
 * this field and only one of them used to know the quirk.
 */
export const formatLastLogin = (value: DateInput, fallback = 'Never'): string => {
  if (isNeverLoggedIn(value)) return fallback;
  return `${dayjs(value).tz(APP_TZ).format(DATETIME_FMT)} ET`;
};

/**
 * "08/13/2026 3:42:11 PM ET" — a timestamp WITH seconds, for the hover tooltips
 * over relative times ("3 minutes ago") on the audit and activity timelines.
 * Seconds are the point there: without them two entries in the same minute are
 * indistinguishable, which is exactly what the tooltip is opened to resolve.
 */
export const formatDateTimeSeconds = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value).tz(APP_TZ);
  return d.isValid() ? `${d.format(DATETIME_FMT_SECONDS)} ET` : 'Invalid date';
};

/** "3:42:11 PM ET" — time-of-day in Eastern, with seconds. */
export const formatTimeSeconds = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value).tz(APP_TZ);
  return d.isValid() ? `${d.format(TIME_FMT_SECONDS)} ET` : 'Invalid date';
};

/** "3:42 PM ET" — time-of-day in Eastern. */
export const formatTime = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value).tz(APP_TZ);
  return d.isValid() ? `${d.format(TIME_FMT)} ET` : 'Invalid date';
};

/**
 * "08/13" — compact date for an INSTANT (its Eastern day, year dropped). The
 * counterpart to `formatStoredDateShort`: use this when the value is a real
 * timestamp (`tickets.sla_due_date`, computed from an SLA window), not a day
 * someone picked. The two differ only when the instant lands on exactly midnight
 * UTC, which is 8 PM ET the evening before — rare, and wrong in the direction
 * that makes a deadline look a day later than it is.
 */
export const formatDateShortInstant = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = parseForDisplay(value);
  return d.isValid() ? d.format(DATE_FMT_SHORT) : 'Invalid date';
};

/**
 * "08/13" — compact date WITHOUT the year, for calendar-day surfaces where the
 * year is implied by context (Kanban / Gantt cards).
 *
 * On the `storedDayIso` rule: its callers are picked calendar days
 * (`tasks.due_date`) sitting at midnight UTC, and reading one as an instant
 * shows the day before. For a real timestamp use `formatDateShortInstant` above
 * — the names are deliberately unlike each other, because an earlier pair
 * (`formatDateShort` / this) was a coin-flip apart at the call site and got
 * picked wrong.
 */
export const formatStoredDateShort = (value: DateInput, fallback = '—'): string =>
  renderStoredDay(value, fallback, DATE_FMT_SHORT);

/**
 * "08/13/2026 14:30 ET" — 24-hour timestamp in Eastern. Same UTC → Eastern
 * conversion as formatDateTime; only the clock format differs (used by the
 * Tools activity / webhook-events reports, which render times in 24h).
 */
export const formatDateTime24 = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value).tz(APP_TZ);
  return d.isValid() ? `${d.format(DATETIME_FMT_24)} ET` : 'Invalid date';
};

/**
 * "08/13/2026 14:30:11 ET" — 24-hour timestamp WITH seconds, for the ops ledgers
 * (inventory transaction log, async event queue) where two rows a second apart
 * are a different story than two rows a minute apart.
 */
export const formatDateTime24Seconds = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value).tz(APP_TZ);
  return d.isValid() ? `${d.format(DATETIME_FMT_24_SECONDS)} ET` : 'Invalid date';
};

/** "14:30:11 ET" — 24-hour time-of-day with seconds, matching the ledger above. */
export const formatTime24Seconds = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value).tz(APP_TZ);
  return d.isValid() ? `${d.format(TIME_FMT_24_SECONDS)} ET` : 'Invalid date';
};

/**
 * "14:30:11.842 ET" — millisecond precision, for the async-event progress log
 * where consecutive entries routinely land inside the same second and the
 * ordering IS the diagnostic.
 */
export const formatTime24Millis = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value).tz(APP_TZ);
  return d.isValid() ? `${d.format(`${TIME_FMT_24_SECONDS}.SSS`)} ET` : 'Invalid date';
};

/**
 * "08/13 3:42 PM ET" — year-less timestamp for chart axes and tooltips, where the
 * window label right beside it already names the year and repeating it costs
 * width that the plot needs.
 */
export const formatDateTimeShort = (value: DateInput, fallback = '—'): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value).tz(APP_TZ);
  return d.isValid() ? `${d.format(DATETIME_FMT_SHORT)} ET` : 'Invalid date';
};

/**
 * Whole days from today (Eastern) to a stored CALENDAR DAY — negative when the
 * day has passed. For deadline maths on the columns `formatStoredDate` renders
 * (`projects.end_date`, `tasks.due_date`).
 *
 * Exists because those two have to agree. Subtracting `Date.now()` from
 * `new Date(end_date)` measures to the stored midnight-UTC INSTANT, which is
 * 8 PM ET the evening before the day the cell displays — so a project shows
 * "1 days overdue" beside an end date of today, from 8 PM the previous evening.
 * Comparing calendar days instead makes the number and the date one statement.
 */
export const daysUntilStoredDay = (value: DateInput): number | null =>
  daysFromToday(storedDayIso(value));

/**
 * Whole days from today (Eastern) to the Eastern day of an INSTANT — the
 * counterpart to `daysUntilStoredDay`, for computed deadlines like a ticket's
 * `sla_due_date`. Reading one of those as a picked day puts an SLA landing on
 * midnight UTC a day LATE, which is the wrong direction for a deadline.
 */
export const daysUntilEtDay = (value: DateInput): number | null =>
  daysFromToday(etDayIso(value));

/**
 * A stored instant as the wall clock an `<input type="datetime-local">` shows —
 * in EASTERN, so the value in the editor matches the value displayed beside it.
 *
 * `toISOString().slice(0, 16)` is the trap this replaces: it seeds the input with
 * the UTC wall clock, so toggling Edit jumps the time by the offset and saving
 * writes back a time the user never chose.
 */
export const etDateTimeInputValue = (value: DateInput, fallback = ''): string => {
  if (value === null || value === undefined || value === '') return fallback;
  const d = dayjs(value).tz(APP_TZ);
  return d.isValid() ? d.format('YYYY-MM-DDTHH:mm') : fallback;
};

/**
 * The reverse: what a `datetime-local` gives back is a bare wall clock with no
 * zone, so READ IT AS EASTERN and store the instant (CLAUDE.md dates rule).
 * `new Date(str)` would read it in the browser's zone instead.
 */
export const etDateTimeInputToIso = (value: string): string | null => {
  if (!value) return null;
  const d = dayjs.tz(value, APP_TZ);
  return d.isValid() ? d.toISOString() : null;
};

/**
 * Whole days until a deadline, dispatching on what the value MEANS.
 *
 * The one definition: `CompactRows` and `MyAssignments/groupItems` each had their
 * own copy of this dispatch, serving disjoint sets of pages, so a change to one
 * silently missed the other's callers — which is how the "1d overdue for a task
 * due today" bug survived being fixed once. Both now re-export this under their
 * existing name, so no call site had to move.
 */
export const daysUntilDue = (
  value: DateInput,
  kind: 'day' | 'instant' = 'day',
): number | null => (kind === 'instant' ? daysUntilEtDay(value) : daysUntilStoredDay(value));

/**
 * Sort rows by a deadline, soonest first, with undated rows trailing.
 *
 * Decorate-sort-undecorate: `getDays` costs a dayjs parse plus a UTC/Eastern
 * conversion plus a format, and a comparator runs O(n log n) times per render —
 * so the key is computed once per row instead. `primaryRank` is an optional
 * higher-priority key applied first, DESCENDING (My Assignments floats
 * `in_progress` above everything regardless of deadline).
 *
 * One definition because there were three, differing only in the field read: a
 * change to how undated rows or ties are ordered reached exactly one of them.
 * `Array.prototype.sort` is stable, so equal keys keep their input order.
 */
export const sortByDeadline = <T,>(
  items: T[],
  getDays: (item: T) => number | null,
  primaryRank?: (item: T) => number,
): T[] => items
  .map((item) => ({ item, rank: primaryRank ? primaryRank(item) : 0, days: getDays(item) }))
  .sort((a, b) => (b.rank - a.rank) || ((a.days ?? Infinity) - (b.days ?? Infinity)))
  .map((d) => d.item);

/**
 * Selection → UTC, interpreted as Eastern (CLAUDE.md dates rule): the exact
 * instant a user-picked YYYY-MM-DD calendar day starts in Eastern time.
 * Use these to build date-range filter bounds — never `new Date(dateStr)` /
 * `setHours(...)`, which silently use the browser's zone and make the same
 * filter match different rows for non-Eastern viewers.
 */
export const etDayStart = (dateStr: string): Date =>
  dayjs.tz(dateStr, APP_TZ).startOf('day').toDate();

/** Inclusive end of an Eastern calendar day (23:59:59.999 ET) as an instant. */
export const etDayEnd = (dateStr: string): Date =>
  dayjs.tz(dateStr, APP_TZ).endOf('day').toDate();

/**
 * "Now", anchored in EASTERN — the single clock every calendar-day derivation
 * should start from.
 *
 * Exists because `dayjs()` is the browser's clock, and a bare `dayjs()` whose
 * result is only ever `.format('YYYY-MM-DD')`'d silently names the browser's
 * calendar day. Feed that to `etDayStart`/`etDayEnd`, which read it back as
 * Eastern, and the window doesn't match its own label: at 10 AM in Tokyo (9 PM
 * ET the day before) "Today" resolves to a day that hasn't begun in Eastern, so
 * the table is empty under a button showing yesterday's date. Anything that
 * needs a Dayjs to do day arithmetic on should start here.
 */
export const etNow = (): Dayjs => dayjs().tz(APP_TZ);

/** Today's calendar date in Eastern, as YYYY-MM-DD (for date-input presets). */
export const etToday = (): string => etNow().format('YYYY-MM-DD');

/**
 * `etToday()` as UTC-epoch millis, memoised for a minute. The day helpers below
 * run inside sort comparators (`sortItems`, the Projects and Tasks lists), so
 * they'd otherwise do a `dayjs().tz()` + `Intl` round trip O(n log n) times per
 * render. A minute of staleness only matters within a minute of Eastern midnight,
 * and the answer there is off by a day for at most that minute.
 */
let todayCache: { minute: number; ms: number } | null = null;
const todayUtcMs = (): number => {
  const minute = Math.floor(Date.now() / 60_000);
  if (!todayCache || todayCache.minute !== minute) {
    const [y, m, d] = etToday().split('-');
    todayCache = { minute, ms: Date.UTC(Number(y), Number(m) - 1, Number(d)) };
  }
  return todayCache.ms;
};

/** Whole days from today (Eastern) to a `YYYY-MM-DD` calendar day. */
const daysFromToday = (iso: string | null): number | null => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  return Math.round((Date.UTC(Number(y), Number(m) - 1, Number(d)) - todayUtcMs()) / 86_400_000);
};

/** The Eastern calendar date `n` days ago, as YYYY-MM-DD. */
export const etDaysAgo = (n: number): string =>
  etNow().subtract(n, 'day').format('YYYY-MM-DD');

/**
 * Person name for STRING contexts — sorting keys, option labels, CSV cells,
 * tooltips. (Display contexts should keep using <EmployeeDisplay>.)
 */
export const formatPersonName = (
  person: { first_name?: string | null; last_name?: string | null } | null | undefined,
  fallback = '—'
): string => {
  const name = [person?.first_name, person?.last_name]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');
  return name || fallback;
};

/**
 * "Last, First" — for surfaces the billers scan by surname (Daily Monitoring).
 * Formats from the name PARTS, never by re-splitting a joined "First Last"
 * string (compound surnames and middle names make that guess wrong). With only
 * one part present it returns that part bare; the comma form needs both.
 */
export const formatNameLastFirst = (
  person: { first_name?: string | null; last_name?: string | null } | null | undefined,
  fallback = '—'
): string => {
  const first = (person?.first_name || '').trim();
  const last = (person?.last_name || '').trim();
  if (last && first) return `${last}, ${first}`;
  return last || first || fallback;
};

/**
 * Format a number or string as USD currency.
 */
export const formatCurrency = (value: number | string | null | undefined): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;
  if (numericValue === null || numericValue === undefined || isNaN(numericValue)) return '—';

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericValue);
};

export type RecurrenceRule = {
  freq?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  interval?: number;
  until?: string | null;
} | null | undefined;

// Mirror of backend describeRecurrence — keep in sync with
// backend/src/services/tasks/recurrence.js.
export const describeRecurrence = (rule: RecurrenceRule): string => {
  if (!rule || !rule.freq) return '';
  const interval = rule.interval && rule.interval > 0 ? rule.interval : 1;
  const unit =
    rule.freq === 'daily' ? 'day'
    : rule.freq === 'weekly' ? 'week'
    : rule.freq === 'monthly' ? 'month'
    : 'year';
  const every = interval === 1 ? `Every ${unit}` : `Every ${interval} ${unit}s`;
  if (!rule.until) return every;
  // The rule's end is a picked calendar DAY — a raw `2026-09-30` inside a chip was
  // the one remaining ISO date on screen. Take the leading `YYYY-MM-DD` off an ISO
  // string so a time-carrying value still names the day the user picked (handing
  // `2026-09-30T02:00:00Z` over whole resolves to its EASTERN day, 09/29), and
  // match it rather than `.split('T')`, which also splits `String(aDate)` on the T
  // in "GMT". Resolve the day before formatting: `formatStoredDate` reports
  // garbage as "Invalid date", and a chip reading "until Invalid date" is worse
  // than no clause at all.
  const untilDay = String(rule.until).match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? rule.until;
  const untilIso = storedDayIso(untilDay);
  // `isoToDisplay`, not `formatStoredDate`: the day is already resolved and
  // validated, and going back through the public helper would re-run
  // `storedDayIso` on its own output for a branch that can no longer be reached.
  return untilIso ? `${every} (until ${isoToDisplay(untilIso)})` : every;
};

/**
 * Abbreviate a company name to its initials for compact chips:
 * "Analyze Pathology" → "AP", "Spector Scientific Lab" → "SSL".
 * Takes the first alphanumeric character of each whitespace-separated word.
 * Always pair with a tooltip showing the full name. Falls back to the trimmed
 * name when no initials can be derived (so the chip is never empty).
 */
export const companyInitials = (name: string | null | undefined): string => {
  const trimmed = (name || '').trim();
  if (!trimmed) return '';
  const letters = trimmed
    .split(/\s+/)
    .map((word) => (word.match(/[A-Za-z0-9]/) || [''])[0])
    .join('')
    .toUpperCase();
  return letters || trimmed;
};
